import {AssaysFeature, LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';

import {
  getBasicCheckFn,
  VariableAmount,
  CHECK_CAN_CONVERT_TO_SCHEMA_FIELDS,
  ONLY_SEND_TO_DOCUMENT_VARIABLES,
  WARN_MULTI_SELECT_PRIMITIVE_FIELD,
  WARN_REQUIRES_COLUMN_MAPPING,
  WARN_STORABLE_VARIABLE_WITHOUT_SCHEMA,
} from './CompatibilityCheckFunctions';
import {VariableType, AnalysisTemplateIntegration} from './CompatibilityCheckTypes';

const ZERO_TABLE_VARIABLES = getBasicCheckFn(VariableType.TABLE, VariableAmount.ZERO);
const EXACTLY_ONE_TABLE_VARIABLE = getBasicCheckFn(VariableType.TABLE, VariableAmount.EXACTLY_ONE);
const ANY_NUMBER_OF_TABLE_VARIABLES = getBasicCheckFn(VariableType.TABLE, VariableAmount.ANY_NUMBER);

const ZERO_FILE_VARIABLES = getBasicCheckFn(VariableType.FILE, VariableAmount.ZERO);
const EXACTLY_ONE_FILE_VARIABLE = getBasicCheckFn(VariableType.FILE, VariableAmount.EXACTLY_ONE);
const ANY_NUMBER_OF_FILE_VARIABLES = getBasicCheckFn(VariableType.FILE, VariableAmount.ANY_NUMBER);

const ZERO_FIELD_VARIABLES = getBasicCheckFn(VariableType.FIELD, VariableAmount.ZERO);
const ANY_NUMBER_OF_FIELD_VARIABLES = getBasicCheckFn(VariableType.FIELD, VariableAmount.ANY_NUMBER);

const APPLY_TO_ANALYSIS_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Apply template in analysis',
  icon: 'lightbulb',
  visible: () => true,
  checks: [
    ANY_NUMBER_OF_TABLE_VARIABLES,
    ANY_NUMBER_OF_FILE_VARIABLES,
    ANY_NUMBER_OF_FIELD_VARIABLES,
    WARN_REQUIRES_COLUMN_MAPPING,
  ],
};

const CONNECTION_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Connection',
  icon: 'plug',
  visible: () => BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION,
  checks: [ZERO_TABLE_VARIABLES, EXACTLY_ONE_FILE_VARIABLE, ZERO_FIELD_VARIABLES],
};

const RUN_SCHEMA_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Run schema',
  icon: 'instrument',
  visible: () => isFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_OPEN}),
  checks: [
    ZERO_TABLE_VARIABLES,
    ANY_NUMBER_OF_FILE_VARIABLES,
    ANY_NUMBER_OF_FIELD_VARIABLES,
    CHECK_CAN_CONVERT_TO_SCHEMA_FIELDS,
    WARN_MULTI_SELECT_PRIMITIVE_FIELD,
    WARN_STORABLE_VARIABLE_WITHOUT_SCHEMA,
  ],
};

const RUN_SCHEMA_OUTPUT_FILE_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Run schema output file',
  icon: 'upload',
  visible: () => isFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_OPEN}),
  checks: [EXACTLY_ONE_TABLE_VARIABLE, ZERO_FILE_VARIABLES, ONLY_SEND_TO_DOCUMENT_VARIABLES],
};

const RECIPE_UNIT_OPERATION_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Recipe unit operation',
  icon: 'mortar-pestle',
  visible: () => isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN}),
  checks: [
    ANY_NUMBER_OF_TABLE_VARIABLES,
    ZERO_FILE_VARIABLES,
    ONLY_SEND_TO_DOCUMENT_VARIABLES,
    WARN_REQUIRES_COLUMN_MAPPING,
  ],
};

const ASSAY_UNIT_OPERATION_INTEGRATION: AnalysisTemplateIntegration = {
  name: 'Assay unit operation',
  icon: 'eye-dropper',
  visible: () => isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN}),
  checks: [
    ANY_NUMBER_OF_TABLE_VARIABLES,
    ZERO_FILE_VARIABLES,
    ONLY_SEND_TO_DOCUMENT_VARIABLES,
    WARN_REQUIRES_COLUMN_MAPPING,
  ],
};

/**
 * All integrations that support building on top of analysis templates, in the order they should be
 * displayed.
 *
 * @package
 */
export const ANALYSIS_TEMPLATE_INTEGRATIONS: Array<AnalysisTemplateIntegration> = [
  APPLY_TO_ANALYSIS_INTEGRATION,
  CONNECTION_INTEGRATION,
  RUN_SCHEMA_INTEGRATION,
  RUN_SCHEMA_OUTPUT_FILE_INTEGRATION,
  ASSAY_UNIT_OPERATION_INTEGRATION,
  RECIPE_UNIT_OPERATION_INTEGRATION,
];
